import InvoiceButtonsAndModals from "@dana/Components/OrderHistory/InvoiceButtonsModals";
import ReturnWizardModal from "@dana/Components/StandardReturns/ReturnWizardModal";
import { OrderTypes } from "@dana/Handlers/ProductAvailabilityHandler";
import InvoiceIcon from "@dana/Icons/InvoiceIcon";
import OrderReturn from "@dana/Icons/OrderReturn";
import { UpdateStandardReturnStateParameter } from "@dana/Services/DanaStandardReturnOrderService";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import getStandardReturnOrder from "@dana/Store/Data/StandardReturns/Handlers/GetStandardReturnOrderHandler";
import resetStandardReturnState from "@dana/Store/Data/StandardReturns/Handlers/ResetStandardReturnStateHandler";
import updateStep from "@dana/Store/Data/StandardReturns/Handlers/UpdateCurrentStepHandler";
import { removeCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { OrdersDataViewContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import exportOrders from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/ExportOrders";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import selectOrders from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/SelectOrders";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { setCookie } from "@insite/mobius/utilities/cookies";
import HistoryContext, { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showOrderNumber = "showOrderNumber",
    showDate = "showDate",
    showOrderTotal = "showOrderTotal",
    showStatus = "showStatus",
    showShipTo = "showShipTo",
    showPONumber = "showPONumber",
    showReorderProducts = "showReorderProducts",
}

type ModalState = {
    isModalOpen: boolean;
};

type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    webOrderNumber: ["webOrderNumber"],
    orderDate: ["orderDate", "erpOrderNumber", "webOrderNumber"],
    orderTotal: ["orderTotal"],
    status: ["status"],
    stCompanyName: ["stCompanyName"],
    customerPO: ["customerPO"],
    customerVatNumber: ["customerVatNumber"],
    orderSource: ["orderSource"],
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showOrderNumber]: boolean;
        [fields.showDate]: boolean;
        [fields.showOrderTotal]: boolean;
        [fields.showStatus]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showReorderProducts]: boolean;
    };
}

const mapStateToProps = (state: ApplicationStateDana) => {
    return {
        parameter: state.pages.orderHistory.getOrdersParameter,
        isReordering: state.pages.orderHistory.isReordering,
        isUpdatingCart: state.context.isUpdatingCart,
        showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
        language: state.context.session.language,
        showPoNumber: getSettingsCollection(state).orderSettings.showPoNumber,
        isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
        selectedOrderIds: state.pages.orderHistory.selectedOrderIds || {},
        showPricing: state.context.showPricing,
        standardReturnState: state.standardReturnState,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    reorder,
    selectOrders,
    exportOrders,
    getStandardReturnOrder,
    updateStep,
    resetStandardReturnState,
};

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface OrderHistoryTableStyles {
    container?: InjectableCss;
    headerClickable?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    reorderHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    customerPOCells?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    reorderCells?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
    returnButton?: ButtonPresentationProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    orderNumberLink?: LinkPresentationProps;
    dataTable?: DataTableProps;
    checkboxHeader?: DataTableHeaderProps;
    checkboxCells?: DataTableCellProps;
    rowCheckbox?: CheckboxPresentationProps;
    headerContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    exportLink?: LinkPresentationProps;
    invoicesButtonsWrapper?: InjectableCss;
    invoicesButtons?: InjectableCss;
    invoiceButton?: ButtonPresentationProps;

}

export const orderHistoryTableStyles: OrderHistoryTableStyles = {
    container: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    headerClickable: {
        css: css`
            width: 100%;
            justify-content: space-between;
        `,
    },
    reorderButton: {
        color: "secondary",
        sizeVariant: "medium",
    },
    returnButton: {
        icon: {
            src: OrderReturn,
            position: "left",
        },
        variant: "secondary",
        typographyProps: { size: 14 },
        css: css`
            padding: 8px;
        `,
      
    },
    reorderButtonSpinner: {
        size: 22,
        css: css`
            margin-left: auto;
            margin-right: auto;
            display: block;
        `,
    },
    shipToHeader: {
        tight: true,
    },
    orderTotalHeader: {
        tight: true,
        alignX: "right",
    },
    reorderHeader: {
        tight: true,
    },
    orderTotalCells: {
        alignX: "right",
    },
    shipToCells: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 300px;
            `,
        },
    },
    checkboxHeader: {
        tight: true,
    },
    headerContainer: {
        gap: 8,
        css: css`
            padding: 20px 0;
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            > * {
                padding-right: 10px;
            }
        `,
    },
    exportLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    invoicesButtonsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 16px;
        `,
    },
   
};

const styles = orderHistoryTableStyles;

class OrderHistoryTable extends React.Component<Props, ModalState> {
    state: ModalState = {
        isModalOpen: false,
    };

    static contextType = OrdersDataViewContext;
    context!: React.ContextType<typeof OrdersDataViewContext>;

    headerClick(sortField: string) {
        const previousSort = this.props.parameter.sort ?? "";
        const [previousPrimaryField] = previousSort.split(",");
        const direction = previousPrimaryField === sortField ? " DESC" : "";
        const sort = sortByFields[sortField].map(field => `${field}${direction}`).join(", ");
        this.props.updateSearchFields({ sort });
    }

    sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (!this.props.parameter.sort) {
            return sorted;
        }
        const [primaryField] = this.props.parameter.sort.split(",");
        if (primaryField === sortField) {
            sorted = "ascending";
        } else if (primaryField === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    reorderClick = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) =>
        this.props.reorder({
            orderNumber,
            onSuccess: () => this.onReorderSuccess(orderNumber, linkOrderNumber, isVmiOrder),
            onError: message => this.onReorderError(message),
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.();
                }
            },
        });

    onReorderSuccess = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) => {
        if (!this.props.showAddToCartConfirmationDialog) {
            return;
        }
        this.props.toaster.addToast({
            body: (
                <HistoryContext.Provider value={{ history: this.props.history }}>
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={linkOrderNumber}
                        isVmiOrder={isVmiOrder}
                        isVmiOrderDetailsPage={this.props.isVmiOrderHistoryPage}
                    />
                    &nbsp;{translate("Added to Cart")}
                </HistoryContext.Provider>
            ),
            messageType: "success",
            timeoutLength: 6000,
        });
    };

    onReorderError = (message: string) => {
        this.props.toaster.addToast({
            body: message,
            messageType: "danger",
            timeoutLength: 6000,
        });
    };

    openReturnModal = () => {
        this.setState({ isModalOpen: true });
    };

    closeReturnModal = () => {
        this.setState({ isModalOpen: false });
        this.props.resetStandardReturnState({ reset: true });
        this.props.updateStep({ currentStep: "SELECT_ITEMS" } as UpdateStandardReturnStateParameter);
    };

    render() {
        const ordersDataView = this.context;
        if (ordersDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingOverlay className="fullLoadingSpinner" />
                </StyledWrapper>
            );
        }

        if (!ordersDataView.value) {
            return null;
        }

        if (ordersDataView.value.length === 0) {
            return (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography as="p" {...styles.noResultsText} data-test-selector="orderHistoryTable_noOrdersFound">
                        {translate("No orders found")}
                    </Typography>
                </StyledWrapper>
            );
        }

        const rows = ordersDataView.value.map(order => {
            return {
                id: order.id,
                linkOrderNumber: order.erpOrderNumber,
                date: order.orderDate
                    ? getLocalizedDateTime({
                          dateTime: new Date(order.orderDate),
                          language: this.props.language,
                      })
                    : "",
                orderNumber: order.webOrderNumber || order.erpOrderNumber,
                statusDisplay: order.statusDisplay,
                status: order.status,
                po: order.customerPO,
                sepo: order.customerVatNumber,
                type:
                    order.properties?.isEmergencyOrder?.toLocaleLowerCase() === "true"
                        ? translate("Emergency")
                        : order.properties?.isAnnualReturnsOrder?.toLocaleLowerCase() === "true"
                        ? translate("Annual Returns")
                        : translate("Stock"),
                isVmiOrder: !!order.vmiLocationId,
                notes:
                    order.terms !== "" && order.terms !== null && order.terms !== undefined
                        ? (() => {
                              try {
                                  return JSON.parse(order.terms);
                              } catch {
                                  return "";
                              }
                          })()
                        : "",
                webOrderNumber: order.webOrderNumber,
            };
        });

        const { showOrderNumber, showDate, showStatus, showPONumber, showReorderProducts } = this.props.fields;

        const isTrueOrUndefined = (value?: boolean) => value === true || value === undefined;

        const checkboxChangeHandler = (value: boolean, id?: string) => {
            if (id) {
                this.props.selectOrders({ ids: [id] });
            } else {
                let ids: string[] = [];
                if (value) {
                    for (const row of this.context.value!) {
                        if (!this.props.selectedOrderIds[row.id]) {
                            ids.push(row.id);
                        }
                    }
                } else {
                    ids = Object.keys(this.props.selectedOrderIds);
                }

                this.props.selectOrders({ ids });
            }
        };

        const handleExportButtonClick = (selectedOnly = false) => {
            this.props.exportOrders({ ids: selectedOnly ? this.props.selectedOrderIds : {} });
        };
        const initStandardReturnHandler = (erpOrderNumber: string) => {
            this.props.getStandardReturnOrder({
                orderNumber: erpOrderNumber,
                onSuccess: response => {
                    this.openReturnModal();
                },
                onError: message => this.onReorderError(message),
            });
        };

        return (
            <LoadingOverlay loading={this.props?.standardReturnState?.isLoading}>
                <StyledWrapper {...styles.container} data-test-selector="orderHistoryTable">
                    {rows.length > 0 && this.props.isVmiOrderHistoryPage && (
                        <GridContainer {...styles.headerContainer}>
                            <GridItem {...styles.gridItem}>
                                <>
                                    <Link
                                        disabled={Object.keys(this.props.selectedOrderIds).length === 0}
                                        onClick={() => handleExportButtonClick(true)}
                                    >
                                        {translate("Export Selected")}
                                    </Link>
                                    <Link {...styles.exportLink} onClick={() => handleExportButtonClick()}>
                                        {translate("Export All")}
                                    </Link>
                                </>
                            </GridItem>
                        </GridContainer>
                    )}
                    <DataTable {...styles.dataTable}>
                        <DataTableHead>
                            {this.props.isVmiOrderHistoryPage && (
                                <DataTableHeader {...styles.checkboxHeader}>
                                    <Checkbox
                                        {...styles.rowCheckbox}
                                        checked={
                                            Object.keys(this.props.selectedOrderIds).length ===
                                            ordersDataView.value.length
                                        }
                                        onChange={(e, value) => checkboxChangeHandler(value)}
                                    />
                                </DataTableHeader>
                            )}
                            {isTrueOrUndefined(showOrderNumber) && (
                                <DataTableHeader
                                    {...styles.orderNumberHeader}
                                    title={translate("Order Number")}
                                    sorted={this.sorted("webOrderNumber")}
                                    onSortClick={() => this.headerClick("webOrderNumber")}
                                    data-test-selector="tableHeader_webOrderNumber"
                                >
                                    {translate("Order No.", "webOrderNumber")}
                                </DataTableHeader>
                            )}
                            {isTrueOrUndefined(showDate) && (
                                <DataTableHeader
                                    {...styles.orderDateHeader}
                                    sorted={this.sorted("orderDate")}
                                    onSortClick={() => this.headerClick("orderDate")}
                                    data-test-selector="tableHeader_OrderDate"
                                >
                                    {translate("Order Date", "orderDate")}
                                </DataTableHeader>
                            )}
                            {isTrueOrUndefined(showStatus) && (
                                <DataTableHeader
                                    {...styles.statusHeader}
                                    sorted={this.sorted("status")}
                                    onSortClick={() => this.headerClick("status")}
                                    data-test-selector="tableHeader_status"
                                >
                                    {translate("Order Status", "status")}
                                </DataTableHeader>
                            )}

                            <DataTableHeader
                                {...styles.statusHeader}
                                sorted={this.sorted("orderSource")}
                                onSortClick={() => this.headerClick("orderSource")}
                                data-test-selector="tableHeader_status"
                            >
                                {translate("Order Type", "orderSource")}
                            </DataTableHeader>

                            {isTrueOrUndefined(showPONumber) && this.props.showPoNumber && (
                                <DataTableHeader
                                    {...styles.customerPOHeader}
                                    title={translate("Purchase Order Number")}
                                    sorted={this.sorted("customerPO")}
                                    onSortClick={() => this.headerClick("customerPO")}
                                >
                                    {translate("Primary PO No.", "customerPO")}
                                </DataTableHeader>
                            )}

                            <DataTableHeader
                                {...styles.customerPOHeader}
                                title={translate("Secondary PO #")}
                                sorted={this.sorted("customerVatNumber")}
                                onSortClick={() => this.headerClick("customerVatNumber")}
                            >
                                {translate("Secondary PO No.", "customerVatNumber")}
                            </DataTableHeader>
                        </DataTableHead>
                        <DataTableBody data-test-selector="orderHistoryTable_tableBody">
                            {rows.map(
                                ({
                                    id,
                                    webOrderNumber,
                                    notes,
                                    linkOrderNumber,
                                    date,
                                    orderNumber,
                                    status,
                                    statusDisplay,
                                    po,
                                    sepo,
                                    type,
                                    isVmiOrder,
                                }) => (
                                    <React.Fragment key={id}>
                                        <DataTableRow>
                                            {this.props.isVmiOrderHistoryPage && (
                                                <DataTableCell tight {...styles.checkboxCells}>
                                                    <Checkbox
                                                        {...styles.rowCheckbox}
                                                        checked={this.props.selectedOrderIds[id]}
                                                        onChange={(e, value) => checkboxChangeHandler(value, id)}
                                                    />
                                                </DataTableCell>
                                            )}
                                            {isTrueOrUndefined(showOrderNumber) && (
                                                <DataTableCell
                                                    tight
                                                    {...styles.orderNumberCells}
                                                    data-test-selector="orderHistoryTable_tableCell_orderNumber"
                                                >
                                                    <OrderDetailPageTypeLink
                                                        title={linkOrderNumber}
                                                        orderNumber={linkOrderNumber}
                                                        isVmiOrder={isVmiOrder}
                                                        isVmiOrderDetailsPage={this.props.isVmiOrderHistoryPage}
                                                    />
                                                    {webOrderNumber && (
                                                        <>
                                                            <Typography className="process-no">
                                                                <strong>{translate("Process Number")}:</strong>{" "}
                                                                {webOrderNumber}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </DataTableCell>
                                            )}
                                            {isTrueOrUndefined(showDate) && (
                                                <DataTableCell
                                                    tight
                                                    {...styles.orderDateCells}
                                                    data-test-selector="orderHistoryTable_tableCell_date"
                                                >
                                                    {date}
                                                </DataTableCell>
                                            )}

                                            {isTrueOrUndefined(showStatus) && (
                                                <DataTableCell
                                                    tight
                                                    {...styles.statusCells}
                                                    data-test-selector="orderHistoryTable_tableCell_status"
                                                >
                                                    <Typography>{statusDisplay}</Typography>
                                                </DataTableCell>
                                            )}

                                            <DataTableCell
                                                tight
                                                {...styles.statusCells}
                                                data-test-selector="orderHistoryTable_tableCell_status"
                                            >
                                                {type ?? translate("N/A")}
                                            </DataTableCell>

                                            {isTrueOrUndefined(showPONumber) && this.props.showPoNumber && (
                                                <DataTableCell tight {...styles.customerPOCells}>
                                                    {po ?? translate("N/A")}
                                                </DataTableCell>
                                            )}

                                            <DataTableCell tight {...styles.customerPOCells}>
                                                {sepo ?? translate("N/A")}
                                            </DataTableCell>
                                        </DataTableRow>
                                        {(statusDisplay === "Completely Shipped" ||
                                            statusDisplay === "Partially Shipped") && (
                                            <DataTableRow>
                                                <DataTableCell colSpan={7}>
                                                    <StyledWrapper {...styles.invoicesButtonsWrapper}>
                                                        <InvoiceButtonsAndModals
                                                            invoiceNumber={notes?.invoiceNumber}
                                                            proformatInvoiceNumber={notes?.proformaInvoice}
                                                            commercialNumber={notes?.commercialInvoice}
                                                            packingSlipNumber={notes?.deliveryNumber}
                                                            orderNumber={linkOrderNumber}
                                                        />
                                                        {this.props.language?.languageCode === "en-us" &&
                                                            (statusDisplay === "Completely Shipped" ||
                                                                statusDisplay === "Partially Shipped") && (
                                                                <Button   {...styles.returnButton}
                                                                    onClick={() =>
                                                                        initStandardReturnHandler(linkOrderNumber)
                                                                    }
                                                                >
                                                                    {translate("Return")}
                                                                </Button>
                                                            )}
                                                    </StyledWrapper>
                                                </DataTableCell>
                                            </DataTableRow>
                                        )}
                                    </React.Fragment>
                                ),
                            )}
                        </DataTableBody>
                    </DataTable>
                    <ReturnWizardModal
                        Modal={Modal}
                        isModalOpen={this.state.isModalOpen}
                        handleModalclose={this.closeReturnModal}
                    />
                </StyledWrapper>
            </LoadingOverlay>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(OrderHistoryTable))),
    definition: {
        group: "Order History",
        displayName: "Search Results Table",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
        fieldDefinitions: [
            {
                name: fields.showOrderNumber,
                displayName: "Order #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.showDate,
                displayName: "Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showOrderTotal,
                displayName: "Order Total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To/Pick Up",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPONumber,
                displayName: "PO #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showReorderProducts,
                displayName: "Reorder",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
        ],
    },
};

export default widgetModule;
